import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

export type ConfirmVariant = 'default' | 'danger' | 'warning';

interface Props {
  /** Titre court affiché dans le header. */
  title: string;
  /** Corps du message — peut contenir des sauts de ligne. */
  message: string;
  /** Libellé du bouton principal (défaut : "Confirmer"). */
  confirmLabel?: string;
  /** Libellé du bouton secondaire (défaut : "Annuler"). */
  cancelLabel?: string;
  /** Coloration : 'danger' (rouge), 'warning' (ambre), 'default' (indigo). */
  variant?: ConfirmVariant;
  /** État de chargement — désactive les boutons. */
  busy?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

const variantStyles: Record<ConfirmVariant, { icon: string; iconBg: string; btn: string; ring: string }> = {
  default: {
    icon: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    iconBg: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-300',
    btn: 'bg-indigo-500 hover:bg-indigo-600',
    ring: 'focus:ring-indigo-400/40',
  },
  warning: {
    icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
    iconBg: 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300',
    btn: 'bg-amber-500 hover:bg-amber-600',
    ring: 'focus:ring-amber-400/40',
  },
  danger: {
    icon: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
    iconBg: 'bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-300',
    btn: 'bg-rose-500 hover:bg-rose-600',
    ring: 'focus:ring-rose-400/40',
  },
};

const ConfirmModal: React.FC<Props> = ({
  title, message,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  variant = 'default',
  busy = false,
  onConfirm, onCancel,
}) => {
  const styles = variantStyles[variant];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (busy) return;
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter')  onConfirm();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel, onConfirm]);

  const content = (
    <div
      className="orion-modal-backdrop fixed inset-0 z-[300] flex items-center justify-center p-4 backdrop-blur-md animate-in fade-in duration-200"
      onClick={busy ? undefined : onCancel}
    >
      <div
        className="orion-modal-panel relative w-full max-w-md rounded-3xl shadow-2xl border overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
      >
        <div className="px-7 pt-7 pb-5">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${styles.iconBg}`}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={styles.icon} />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 id="confirm-modal-title" className="text-lg font-extrabold text-slate-900 dark:text-[#e0eaff] tracking-tight">
                {title}
              </h3>
              <p className="mt-2 text-sm text-slate-600 dark:text-[#a6b7e0] leading-relaxed whitespace-pre-line">
                {message}
              </p>
            </div>
          </div>
        </div>

        <div className="px-7 py-4 bg-slate-50 dark:bg-[rgba(100,140,210,0.05)] border-t border-slate-100 dark:border-[rgba(100,140,210,0.15)] flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className={`text-xs font-bold py-2.5 px-4 rounded-xl text-slate-600 dark:text-[#a6b7e0] hover:bg-slate-200/60 dark:hover:bg-[rgba(100,140,210,0.1)] transition-colors disabled:opacity-40 focus:outline-none focus:ring-2 ${styles.ring}`}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => !busy && onConfirm()}
            disabled={busy}
            autoFocus
            className={`text-xs font-black uppercase tracking-widest py-2.5 px-5 rounded-xl text-white shadow-md ${styles.btn} transition-colors disabled:opacity-40 focus:outline-none focus:ring-2 ${styles.ring}`}
          >
            {busy ? '…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default ConfirmModal;
